import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Calendar from 'ui/components/shared/Calendar';
import { useReportCreation } from 'hooks/useReportCreation';

interface ReportCreationModalProps {
    open: boolean;
    onClose: () => void;
    classId: string;
    onReportCreated: (reportId: string) => void;
}


const ReportCreationModal: React.FC<ReportCreationModalProps> = ({
    open,
    onClose,
    classId,
    onReportCreated
}) => {
    const [selectedStartDate, setSelectedStartDate] = React.useState<string | null>(null);
    const [selectedEndDate, setSelectedEndDate] = React.useState<string | null>(null);

    const { isLoading, error, createReport, clearError } = useReportCreation({
        apiEndpoint: 'reports',
        queryKeyToInvalidate: 'user_reports',
        buildRequestUrl: (params) => `reports?class_uid=${params.classId}&start=${params.startDate}&end=${params.endDate}`,
        onSuccess: onReportCreated
    });

    const handleDateRangeChange = React.useCallback((startDate: string | null, endDate: string | null) => {
        setSelectedStartDate(startDate);
        setSelectedEndDate(endDate);
    }, []);

    const dateRangeText = React.useMemo(() => {
        if (selectedStartDate && selectedEndDate) {
            return `${selectedStartDate} - ${selectedEndDate}`;
        } else if (selectedStartDate) {
            return selectedStartDate;
        } else {
            return '';
        }
    }, [selectedStartDate, selectedEndDate]);

    const handleCreateReport = () => {
        if (!selectedStartDate || !selectedEndDate) {
            return;
        }
        
        createReport({
            classId,
            startDate: selectedStartDate,
            endDate: selectedEndDate
        });
    };

    const handleClose = () => {
        if (!isLoading) {
            clearError();
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: 552,
                    minHeight: 600,
                    maxHeight: '90vh',
                    maxWidth: 'none',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
            sx={{
                '& .MuiBackdrop-root': {
                    backgroundColor: 'rgba(0, 0, 0, 0.5)'
                }
            }}
        >
            <DialogTitle sx={{ pb: 2 }}>
                Choose period
            </DialogTitle>
            
            <DialogContent sx={{ p: 2, overflow: 'hidden' }}>
                {/* Date range display */}
                <Box sx={{ mb: 3 }}>
                    <Box
                        sx={{
                            width: '100%',
                            p: 2,
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            backgroundColor: '#f5f5f5',
                            minHeight: 48,
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'default'
                        }}
                    >
                        <Typography variant="body1" color={dateRangeText ? 'text.primary' : 'text.secondary'}>
                            {dateRangeText || 'Pick a date range'}
                        </Typography>
                    </Box>
                </Box>

                {/* Calendar */}
                <Calendar onDateRangeChange={handleDateRangeChange} />

                {error && (
                    <Alert severity="error" sx={{ mt: 3 }}>
                        {error}
                    </Alert>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 2,
                        mt: 1,
                        pt: 2,
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        disabled={isLoading}
                        sx={{ 
                            color: 'text.secondary',
                            borderColor: 'text.secondary'
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateReport}
                        disabled={isLoading || !selectedStartDate || !selectedEndDate}
                        sx={{ 
                            minWidth: 120,
                            position: 'relative'
                        }}
                    >
                        {isLoading ? (
                            <CircularProgress size={20} sx={{ color: 'white' }} />
                        ) : (
                            'Create report'
                        )}
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default ReportCreationModal;